/**
 * /projects — top of the project navigation.
 *
 * Lists every project in the active org with a Create button. Each row is a
 * link to /projects/:projectSlug where envs live. There's no concept of an
 * "active" project here anymore — selection is URL-driven from this page
 * down.
 */

import { PageHeader } from "@/components/PageHeader";
import { CreateProjectModal } from "@/components/ProjectEnvModals";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { WardkeyAPIError, useActiveOrg, useApiQuery } from "@/lib/api";
import type { ProjectRecord } from "@wardkey/sdk";
import { ArrowRight, Folder } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

export function ProjectsRoute() {
  const activeOrg = useActiveOrg();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const projects = useApiQuery(["projects", activeOrg.org_id], (client) =>
    client.projects.list(activeOrg.org_id),
  );

  const items = projects.data?.projects ?? [];

  return (
    <div className="fade-up mx-auto max-w-[1200px]">
      <PageHeader
        title="Projects"
        subtitle={
          !projects.isLoading && !projects.error ? (
            <span className="font-mono text-[12px] text-muted-foreground">
              {items.length} in {activeOrg.name}
            </span>
          ) : undefined
        }
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            New project
            <span aria-hidden>→</span>
          </Button>
        }
      />

      <p className="resource-description">
        A place for every app. Organize your environments and keep each project's secrets together.
      </p>
      <ProjectsList
        isLoading={projects.isLoading}
        error={projects.error}
        items={items}
        onRefetch={() => projects.refetch()}
        onCreate={() => setCreating(true)}
      />

      {creating && (
        <CreateProjectModal
          onClose={() => setCreating(false)}
          onCreated={(record) => {
            setCreating(false);
            navigate(`/projects/${encodeURIComponent(record.project_slug)}`);
          }}
        />
      )}
    </div>
  );
}

function ProjectsList({
  isLoading,
  error,
  items,
  onRefetch,
  onCreate,
}: {
  isLoading: boolean;
  error: Error | null;
  items: ReadonlyArray<ProjectRecord>;
  onRefetch: () => void;
  onCreate: () => void;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-px">
        {Array.from({ length: 4 }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no stable id
          <Skeleton key={i} className="h-11 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-destructive">
          load failed
        </div>
        <p className="mt-2 text-[14px] text-foreground">
          {error instanceof WardkeyAPIError ? error.message : error.message || "Request failed"}
        </p>
        <Button variant="outline" size="sm" onClick={onRefetch} className="mt-3">
          Retry
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card/40 p-12 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          no projects yet
        </div>
        <h2 className="mt-3 text-lg font-medium text-foreground">Create your first project</h2>
        <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground">
          A project groups one app's environments and secrets. Most teams start with one project per
          service — e.g. <code className="font-mono text-foreground">api</code>,{" "}
          <code className="font-mono text-foreground">web</code>.
        </p>
        <Button size="sm" onClick={onCreate} className="mt-5">
          New project
          <span aria-hidden>→</span>
        </Button>
      </div>
    );
  }

  return (
    <ul className="resource-grid">
      {items.map((p) => (
        <li key={p.project_slug}>
          <Link to={`/projects/${encodeURIComponent(p.project_slug)}`} className="resource-card">
            <span className="project-icon">
              <Folder size={21} strokeWidth={1.5} />
            </span>
            <h2>{p.display_name || p.project_slug}</h2>
            <code>{p.project_slug}</code>
            <footer>
              <span>Explore environments</span>
              <ArrowRight size={17} />
            </footer>
          </Link>
        </li>
      ))}
    </ul>
  );
}
